import type { NotificationSettingsView } from "@planerius/contracts/cabinet-notifications"
import type { ReactNode } from "react"
import { useCallback, useEffect, useState } from "react"

import {
  fetchNotificationSettings,
  updateNotificationRule,
  updateQuietHours,
} from "../../../api/notifications.api"
import type { ApiProblem } from "../../../api/problem"
import { isApiProblem } from "../../../api/problem"
import { cx } from "../../../lib/cx"
import { ProblemScreen, Skeleton } from "../../../shell/states"
import { StandHeader } from "../../directories/stand"
import { Badge, Button, TextField } from "../../../ui"
import {
  MATRIX_CASCADE_NOTE,
  cellOn,
  channelHead,
  channelsWarning,
  matrixRows,
} from "./rules-model"
import styles from "./rules.module.css"

/**
 * 4-Б5 «Уведомления» — /settings/notifications.
 *
 * ЧТО САЛОН РЕШАЕТ ЗДЕСЬ. Два вопроса, и оба про деньги и про клиентов: какие события
 * вообще отправлять и какими каналами. Всё остальное на экране — объяснения, без которых
 * решение принимается неверно: цепочка каскада (иначе салон ставит галочки во все пять
 * каналов «чтобы надёжнее» и получает счёт за SMS), замки на обязательных событиях
 * и честное «канал не подключён» вместо активной клетки.
 *
 * ПОЧЕМУ ТАБЛИЦА, А НЕ СПИСОК КАРТОЧЕК. Решение здесь принимается СРАВНЕНИЕМ: «напоминание
 * за сутки — по боту, за два часа — и по SMS тоже». В списке карточек такое сравнение
 * требует памяти, в таблице оно видно глазом. Узкую ширину таблица переживает прокруткой
 * с залипшим первым столбцом: имя события обязано остаться на экране, иначе человек
 * не знает, какую строку правит.
 *
 * НЕТ ЗДЕСЬ ТОГО, ЧЕГО НЕТ В ПРОДУКТЕ: цены SMS, остатка пакета и кнопки «Пополнить»
 * (оператор не выбран — вопрос владельцу), а также столбца «Звонок с кодом» (flash-call
 * исключён из каскада уведомлений решением ADR-037 п.1: он не переносит текст).
 */
export function NotificationRulesScreen(): ReactNode {
  const [settings, setSettings] = useState<NotificationSettingsView | null>(null)
  // Отказ храним ЦЕЛИКОМ, а не строкой: экран сбоя рисуется по разобранному отказу
  // (код, заголовок, объяснение), и вытащив из него одну строку, мы теряем и «Повторить»,
  // и различие между «нет связи» и «нет права».
  const [failure, setFailure] = useState<ApiProblem | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busyRule, setBusyRule] = useState<string | null>(null)
  const [quietStart, setQuietStart] = useState("")
  const [quietEnd, setQuietEnd] = useState("")
  const [quietSaved, setQuietSaved] = useState(false)

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const next = await fetchNotificationSettings(signal)
      setSettings(next)
      setQuietStart(next.quietHours.start)
      setQuietEnd(next.quietHours.end)
      setProblem(null)
      setFailure(null)
    } catch (error) {
      if (signal?.aborted === true) return
      if (!isApiProblem(error)) throw error
      setFailure(error)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => {
      controller.abort()
    }
  }, [load])

  /**
   * Правка строки: сохраняем и подставляем ответ сервера.
   *
   * Ответ, а не своё предположение: сервер отвергает выключение обязательного события
   * и выбор неподключённого канала, и «оптимистичная» отметка показала бы галочку там,
   * где её нет. Один раз увиденная и исчезнувшая галочка стоит дороже задержки в 200 мс.
   */
  const patchRule = async (
    ruleId: string,
    patch: { readonly enabled?: boolean; readonly channels?: readonly string[] },
  ): Promise<void> => {
    setBusyRule(ruleId)
    setProblem(null)
    try {
      const updated = await updateNotificationRule(ruleId, {
        ...(patch.enabled === undefined ? {} : { enabled: patch.enabled }),
        ...(patch.channels === undefined
          ? {}
          : { channels: patch.channels as NotificationSettingsView["rules"][number]["channels"] }),
      })
      setSettings((current) =>
        current === null
          ? current
          : { ...current, rules: current.rules.map((rule) => (rule.id === ruleId ? updated : rule)) },
      )
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusyRule(null)
    }
  }

  const toggleCell = async (ruleId: string, channel: string): Promise<void> => {
    const rule = settings?.rules.find((item) => item.id === ruleId)
    if (rule === undefined || settings === null) return
    /*
     * ПУСТОЙ ПЕРЕЧЕНЬ = ВСЕ КАНАЛЫ, и первое снятие галочки обязано это раскрыть.
     *
     * Иначе получалось бы так: у правила пусто, салон снимает SMS — и сервер получает
     * список с одним снятым каналом... из пустоты, то есть по-прежнему «все». Поэтому
     * перед снятием перечень разворачивается в подключённые каналы, и только потом
     * из него убирается один.
     */
    const configured = settings.channels.filter((item) => item.configured).map((item) => item.channel)
    const current = rule.channels.length === 0 ? configured : rule.channels
    const next = cellOn(rule, channel as (typeof configured)[number])
      ? current.filter((item) => item !== channel)
      : [...current, channel as (typeof configured)[number]]
    await patchRule(ruleId, { channels: next })
  }

  const saveQuiet = async (): Promise<void> => {
    setProblem(null)
    setQuietSaved(false)
    try {
      const saved = await updateQuietHours({ start: quietStart, end: quietEnd })
      setSettings((current) => (current === null ? current : { ...current, quietHours: saved }))
      setQuietSaved(true)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    }
  }

  if (settings === null) {
    return failure === null ? (
      <Skeleton width="100%" height={320} />
    ) : (
      <ProblemScreen problem={failure} onRetry={() => void load()} />
    )
  }

  const rows = matrixRows(settings)
  const warning = channelsWarning(settings)

  return (
    <>
    {/*
      * Заголовок подраздела — тот же, что у «Правил записи» (StandHeader), и так же СНАРУЖИ
      * полей страницы: это полоса во всю ширину, а не карточка. Без него экран начинался
      * сразу с серой справки о каскаде, и человек, пришедший из «Настроек», не видел,
      * где он: в шапке кабинета стоит лишь общее «Настройки».
      */}
    <StandHeader title="Уведомления клиентам" />
    <div className={styles["page"]}>
      <div className={styles["note"]}>{MATRIX_CASCADE_NOTE}</div>
      {warning === null ? null : (
        <div className={styles["warning"]} role="status">
          {warning}
        </div>
      )}
      {problem === null ? null : (
        <div className={styles["error"]} role="alert">
          {problem}
        </div>
      )}

      <div className={styles["scroll"]}>
        <table className={styles["table"]}>
          <thead>
            <tr>
              <th className={styles["eventHead"]}>Событие</th>
              <th className={styles["kindHead"]}>Вид</th>
              {settings.channels.map((channel) => {
                const head = channelHead(channel)
                return (
                  <th key={channel.channel} className={styles["channelHead"]}>
                    <span>{head.label}</span>
                    <span className={styles["channelSub"]}>{head.sub}</span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={row.enabled ? undefined : styles["rowOff"]}>
                <th scope="row" className={styles["eventCell"]}>
                  <label className={styles["eventLabel"]}>
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      disabled={row.locked || busyRule === row.id}
                      // Замок объясняется словами рядом, а не только отключённой галочкой:
                      // «почему не нажимается» — вопрос, который иначе приезжает в поддержку.
                      title={
                        row.pendingReason ??
                        (row.locked ? "Это сообщение отключить нельзя" : undefined)
                      }
                      onChange={(event) => void patchRule(row.id, { enabled: event.target.checked })}
                    />
                    <span className={styles["eventName"]}>{row.label}</span>
                  </label>
                  {/*
                   * СТРОКА БЕЗ ИСТОЧНИКА ОБЪЯСНЯЕТ СЕБЯ САМА. Иначе салон включает отзывы,
                   * ничего не получает и приходит с вопросом «почему не работает» —
                   * а правильный ответ «события пока никто не создаёт» на экране
                   * не написан нигде.
                   */}
                  {row.pendingReason === null ? (
                    row.hint === null ? null : (
                      <span className={styles["eventHint"]}>{row.hint}</span>
                    )
                  ) : (
                    <span className={styles["eventPending"]}>{row.pendingReason}</span>
                  )}
                </th>
                <td className={styles["kindCell"]}>
                  <Badge tone={row.kindWord === "рекламное" ? "warn" : "gray"}>{row.kindWord}</Badge>
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.channel} className={styles["cell"]}>
                    <button
                      type="button"
                      className={cx(
                        styles["box"],
                        cell.on ? styles["boxOn"] : undefined,
                        cell.disabled ? styles["boxOff"] : undefined,
                      )}
                      disabled={cell.disabled || busyRule === row.id}
                      title={cell.note}
                      aria-label={`${row.label} · ${cell.note}`}
                      aria-pressed={cell.on}
                      onClick={() => void toggleCell(row.id, cell.channel)}
                    >
                      {cell.on ? "✓" : ""}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className={styles["quiet"]}>
        <h2 className={styles["quietTitle"]}>Тихие часы · по времени филиала</h2>
        <div className={styles["quietRow"]}>
          <span>с</span>
          <TextField label="Начало" type="time" value={quietStart} onChange={(event) => setQuietStart(event.target.value)} />
          <span>до</span>
          <TextField label="Конец" type="time" value={quietEnd} onChange={(event) => setQuietEnd(event.target.value)} />
          <Button
            variant="primary"
            deniedReason={
              quietStart === quietEnd ? "Начало и конец совпадают: разведите их" : undefined
            }
            onClick={() => void saveQuiet()}
          >
            Сохранить
          </Button>
        </div>
        {quietSaved ? <div className={styles["saved"]}>Тихие часы сохранены.</div> : null}
        <p className={styles["quietHint"]}>
          Напоминания за два часа и сообщения об отмене уходят всегда — без них клиент
          не придёт или приедет к закрытой двери. Остальное копится и уходит утром.
        </p>
      </section>
    </div>
    </>
  )
}
